import { SpecSheetImages } from './SpecSheetImages';

export class SpecSheet {
	constructor(productID) {
		window.pdfMake.fonts = {
			Roboto: {
				normal: 'Roboto-Regular.ttf',
				bold: 'Roboto-Medium.ttf',
				italics: 'Roboto-Italic.ttf',
				bolditalics: 'Roboto-MediumItalic.ttf',
			},
			helveticaNeue: {
				normal: `${window.location.origin}/wp-content/themes/dialight/core/fonts/HelveticaNeueLTStd-Lt.ttf`,
				bold: `${window.location.origin}/wp-content/themes/dialight/core/fonts/HelveticaNeueLTStd-Bd.ttf`,
				italics: `${window.location.origin}/wp-content/themes/dialight/core/fonts/HelveticaNeueLTStd-It.ttf`,
				bolditalics: `${window.location.origin}/wp-content/themes/dialight/core/fonts/HelveticaNeueLTStd-Bd.ttf`,
			},
			avantGarde: {
				normal: `${window.location.origin}/wp-content/themes/dialight/core/fonts/ITCAvantGardePro-Demi.ttf`,
				bold: `${window.location.origin}/wp-content/themes/dialight/core/fonts/ITCAvantGardePro-Bold.ttf`,
				italics: `${window.location.origin}/wp-content/themes/dialight/core/fonts/HelveticaNeueLTStd-It.ttf`,
				bolditalics: `${window.location.origin}/wp-content/themes/dialight/core/fonts/HelveticaNeueLTStd-Bd.ttf`,
			},
		};

		this.productID = productID;

		this.selectedImages = new SpecSheetImages(productID);

		this.formattedImages = this.selectedImages.formattedImages;
	}

	formatControlsInfo = (data) => {
		const controlsContent = [];
		controlsContent.push({
			text: `${data.heading}`,
			style: 'detailHeader',
			margin: [10, 15, 0, 7],
		});

		for (let i = 0; i < data.content.length; i++) {
			let column = '';
			if (data.content[i].length > 1) {
				const contentSplit = data.content[i][1].split('|');
				const contentStack = [];

				if (contentSplit.length > 1) {
					for (let j = 0; j < contentSplit.length; j++) {
						contentStack.push({ text: `${contentSplit[j]}` });
					}
				} else {
					contentStack.push({ text: `${data.content[i][1]}` });
				}

				column = {
					columns: [
						{
							width: 100,
							text: `${data.content[i][0]}:`,
							style: 'columnHeader',
						},
						{
							width: 120,
							stack: contentStack,
							style: 'columnContent',
							margin: [3, 0, 15, 0],
						},
					],
					columnGap: 5,
					margin: [10, 3, 0, 3],
				};
				controlsContent.push(column);
			} else {
				column = {
					columns: [
						{
							width: 150,
							text: `${data.content[i]}`,
							style: 'columnContent',
							margin: [0, 0, 10, 0],
						},
					],
					columnGap: 5,
					margin: [10, 3, 0, 3],
				};
				controlsContent.push(column);
			}
		}
		return controlsContent;
	};

	formatGroupContent = (data) => {
		const groupContent = [];
		let newContent = '';
		for (const [subhead, content] of Object.entries(data)) {
			if ('string' === typeof content) {
				newContent = content.replace(/&quot;/g, '"');
			}

			let column = {
				columns: [
					{
						width: 100,
						text: `${subhead}:`,
						style: 'columnHeader',
					},
					{
						width: 120,
						text: `${newContent}`,
						style: 'columnContent',
						margin: [0, 0, 10, 0],
					},
				],
				columnGap: 5,
				margin: [10, 3, 0, 3],
			};
			groupContent.push(column);
		}
		return groupContent;
	};

	formatSpecDataContent = (content) => {
		let formattedContent = [];
		for (const [group, data] of Object.entries(content)) {
			if ('table_data' !== group) {
				let heading = group.replace('_', ' ');
				heading = heading
					.split(' ')
					.map((string) => string.charAt(0).toUpperCase() + string.substring(1))
					.join(' ');
				if ('Controls' === heading) {
					if (Object.keys(this.productID.controls).length) {
						const controlsContent = this.formatControlsInfo(
							this.productID.controls,
						);
						formattedContent = formattedContent.concat(controlsContent);
					}
				} else {
					if ('Mechanical Information' === heading) {
						formattedContent.push({
							text: `${heading}`,
							style: 'detailHeader',
							margin: [10, 15, 0, 5],
						});
					} else {
						formattedContent.push({
							text: `${heading}`,
							style: 'detailHeader',
							margin: [10, 10, 0, 7],
						});
					}

					const groupContent = this.formatGroupContent(data);
					formattedContent = formattedContent.concat(groupContent);
				}
			}
		}

		formattedContent.push({
			text: 'All values typical unless otherwise stated (tolerance +/- 10%)',
			style: 'disclaimer',
		});

		return formattedContent;
	};

	getFootnotes = () => {
		const footnotes = {
			page1: {
				absolutePosition: { x: 40, y: 780 },
				text: 'WARNING - INSTALLATION & SECONDARY RETENTION. Use of any Dialight products without proper installation (including secondary retention / netting) and periodic inspections could cause severe injury or death. Dialight recommends that all installations should use secondary retention / netting (appropriate to the installation environment) where applicable. It is the exclusive responsibility of the contractor, installer and/or end-user to: (a) determine the suitability of the product for its intended application; and, (b) ensure that the product is safely installed (with secondary retention / netting where appropriate) and in compliance with all applicable laws and regulations. To the extent permissible under applicable laws, Dialight disclaims all liability for personal injury and/or other damage resulting from any dislodgment or other dislocation of its products.',
				style: 'pageFootnote',
				pageBreak: 'after',
			},
			page2: {
				text: 'DISCLAIMER. All product information provided is, to the best of Dialight’s knowledge, accurate as of the date of publication. When ordering, refer to www.dialight.com for current versions of: (a) relevant product documentation (including the relevant product data sheets); (b) Dialight terms and conditions of sale; and, (c) the relevant product warranty. To the extent that any contract is deemed formed between Dialight and the purchaser of Dialight products and/or an end-user, versions of documents available at www.dialight.com as at the date of sale shall be the versions incorporated therein. In the event of any discrepancy between this document or information provided at www.dialight.com, the latter shall prevail.',
				style: 'pageFootnote',
				absolutePosition: { x: 40, y: 780 },
			},
		};
		return footnotes;
	};

	formatPageOne = (formattedContent) => {
		const certsOdd = this.productID.certification
			.filter((item, index) => index % 2 !== 0)
			.map((item) => ({
				text: `${item.replace(/&lt;/g, '<').replace(/&gt;/g, '>')}`,
				style: 'splitListItem',
			}));
		const certsEven = this.productID.certification
			.filter((item, index) => index % 2 === 0)
			.map((item) => ({
				text: `${item.replace(/&lt;/g, '<').replace(/&gt;/g, '>')}`,
				style: 'splitListItem',
			}));

		const certTable = {
			style: 'specSheet',
			table: {
				widths: [275, 250],
				heights: [0, 600],
				body: [
					['', ''],
					[
						[
							this.formattedImages.pageOneImage,
							{
								text: '* This product image may not be exact to configuration.',
								fontSize: 8,
								bold: true,
								italics: true,
								margin: [10, 0, 0, 5],
								alignment: 'left',
							},
							{
								style: 'infoTable',
								table: {
									widths: [70, 65, 90, 0],
									body: [
										[
											{
												text: 'Project Information',
												colSpan: 4,
												alignment: 'center',
												fillColor: `${this.brandingColor}`,
												color: '#fff',
											},
											'',
											'',
											'',
										],
										[
											{ text: 'Part Number' },
											{ text: `${this.productID.productNumber}`, colSpan: 3 },
										],
										[{ text: 'Project' }, { text: '', colSpan: 3 }],
										[
											{ text: 'Fixture Type' },
											{ text: '' },
											{ text: 'Date', colSpan: 2 },
										],
									],
								},
							},
							{
								style: 'infoTable',
								table: {
									widths: [60, 55, 55, 55],
									heights: [20, 20, 20, 20, 20],
									body: [
										[
											{
												text: 'Specifications',
												colSpan: 4,
												alignment: 'center',
												fillColor: `${this.brandingColor}`,
												color: '#fff',
												margin: [0, 2, 0, 0],
											},
											'',
											'',
											'',
										],
										[{ text: '', colSpan: 4 }, '', '', ''],
										[{ text: '', colSpan: 4 }, '', '', ''],
										[{ text: '', colSpan: 4 }, '', '', ''],
										[{ text: '', colSpan: 4 }, '', '', ''],
									],
								},
							},
							{
								text: 'Certifications & Ratings',
								style: 'header',
								margin: [0, 15, 0, 0],
							},
							{
								alignment: 'justify',
								columns: [
									{
										ul: certsEven,
									},
									{
										ul: certsOdd,
									},
								],
							},
						],
						{
							stack: [formattedContent],
							fillColor: '#d1d2d3',
						},
					],
				],
			},
			layout: 'noBorders',
		};

		return certTable;
	};

	formatOrderInformation = (tableData) => {
		let productLMW = '';
		if (this.productID.lumens > 0 && this.productID.wattage > 0) {
			productLMW = Math.ceil(this.productID.lumens / this.productID.wattage);
		}
		const orderTable = [
			{ text: 'Ordering Information', style: 'header', margin: [0, 0, 0, 5] },
			{
				table: {
					widths: [100, 30, 30, 30, 30, 50, 70, 70, 45],
					heights: [15, 25, 25, 25, 25, 25, 25, 25, 25, 25],
					body: [
						[
							{
								text: `${this.productID.configName} - ${tableData['Operating Voltage']}`,
								colSpan: 9,
								alignment: 'center',
								fillColor: `${this.brandingColor}`,
								color: '#fff',
							},
							'',
							'',
							'',
							'',
							'',
							'',
							'',
							'',
						],
						[
							{
								text: 'Part Number',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'Lumens',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'Wattage',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'lm/W',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'CCT',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'Lens',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'Beam Distribution',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'Mounting Options',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
							{
								text: 'Power Cable',
								alignment: 'center',
								fillColor: '#d1d2d3',
								color: '#43454b',
								bold: true,
								fontSize: 7,
								margin: [0, 7, 0, 0],
							},
						],
						[
							{
								text: `${this.productID.productNumber}`,
								style: 'tableContent',
							},
							{
								text: `${this.productID.lumens.toLocaleString()}`,
								style: 'tableContent',
							},
							{
								text: `${this.productID.wattage.toLocaleString()}`,
								style: 'tableContent',
							},
							{ text: `${productLMW.toLocaleString()}`, style: 'tableContent' },
							{ text: `${tableData['CCT & CRI']}`, style: 'tableContent' },
							{ text: `${tableData['Lens Option']}`, style: 'tableContent' },
							{ text: `${tableData['Optics']}`, style: 'tableContent' },
							{
								text: `${tableData['Mounting Options']}`,
								style: 'tableContent',
							},
							{
								text: `${tableData['Hardware/Cable Options']}`,
								style: 'tableContent',
							},
						],
					],
				},
				layout: {
					hLineColor: '#899089',
					vLineColor: '#899089',
				},
			},
			{
				text: 'All values typical unless otherwise stated (tolerance +/- 10%)',
				style: 'contentFootnote',
				margin: [0, 2, 0, 15],
			},
		];

		return orderTable;
	};

	formatSpecSheetImages = () => {
		let diagramData = '';
		let additionalData = '';

		if (this.selectedImages.images.diagramAlt) {
			diagramData = [
				{
					text: 'Dimensional Drawings',
					style: 'header',
					margin: [0, 5, 0, 15],
				},
				{
					columns: [
						this.formattedImages.dimensional,
						this.formattedImages.dimensionalAlt,
					],
				},
				{
					text: 'Dimensions in inches (mm)',
					style: 'contentFootnote',
					margin: [0, 2, 0, 15],
				},
			];
		} else {
			diagramData = [
				{
					text: 'Dimensional Drawings',
					style: 'header',
					margin: [0, 5, 0, 15],
				},
				this.formattedImages.dimensional,
				{
					text: 'Dimensions in inches (mm)',
					style: 'contentFootnote',
					margin: [0, 2, 0, 15],
				},
			];
		}

		if (this.selectedImages.images.beamAlt) {
			additionalData = [
				{
					text: 'Beam Distribution',
					style: 'header',
					margin: [0, 5, 0, 15],
				},
				{
					columns: [
						this.formattedImages.beamDiagram,
						this.formattedImages.beamDiagramAlt,
					],
				},
				{
					text: 'Dimensions in inches (mm)',
					style: 'contentFootnote',
					margin: [0, 2, 0, 15],
				},
			];
		} else {
			additionalData = [
				{ text: 'Beam Distribution', style: 'header', margin: [0, 5, 0, 15] },
				this.formattedImages.beamDiagram,
				{
					text: 'Please reference spec sheet at www.dialight.com for additional dimensional drawings and replacement parts.',
					style: 'imageDesc',
					margin: [0, 2, 0, 5],
				},
				{
					text: 'Need help? Tech help line: (732) 919-3119 Email: info@dialight.com Website: www.dialight.com',
					fontSize: 10,
					margin: [0, 0, 0, 10],
				},
			];
		}

		const imageData = diagramData.concat(additionalData);

		return imageData;
	};

	formatPage2Header = () => {
		const page2Header = {
			columns: [
				{
					width: '*',
					stack: [
						{ text: `${this.productID.configName}`, style: 'specTitle' },
						{ text: `${this.productID.productNumber}`, style: 'subSpecTitle' },
					],
				},
				this.formattedImages.pageTwoHeader,
			],
		};

		return page2Header;
	};

	parseSpecSheet = (content) => {
		const specContent = [];
		const formattedContent = this.formatSpecDataContent(content);
		const footnotes = this.getFootnotes();

		specContent.push({
			text: `${this.productID.configName}`,
			style: 'specTitle',
		});

		// Check both the 12th partnum and item description- if either is false we want to display the subtitle.
		if (this.productID.selectedOptions[12].partnum !== 'N') {
			console.log('not N: ' + this.productID.selectedOptions[12].partnum);
			if (!this.productID.specData[12].desc.includes('No Battery Backup')) {
				console.log(
					'not No Battery Backup: ' + this.productID.specData[12].desc,
				);
				specContent.push({ text: `Battery Backup`, style: 'subSpecTitle' });
			}
		}
		specContent.push(
			{ text: `${this.productID.productNumber}`, style: 'subSpecTitle' },
			this.formatPageOne(formattedContent),
			footnotes.page1,
			this.formatPage2Header(),
			this.formatOrderInformation(content.table_data),
			this.formatSpecSheetImages(),
			footnotes.page2,
		);

		return specContent;
	};

	specSheetContent = () => {
		const formattedSpecSheet = {
			mechanical_information: {},
			controls: {},
			electrical_specifications: {},
			construction: {},
			photometric_information: [],
			table_data: {},
		};

		formattedSpecSheet.mechanical_information['Fixture weight'] =
			`${this.productID.weightLB} lbs (${this.productID.weightKG}kg)`;
		formattedSpecSheet.mechanical_information['Shipping weight'] =
			`${this.productID.shippingWeightLB} lbs (${this.productID.shippingWeightKG}kg)`;
		formattedSpecSheet.electrical_specifications[
			'Total system power consumption'
		] = `${this.productID.wattage.toLocaleString()}W`;

		for (const [key, value] of Object.entries(this.productID.specData)) {
			for (const [heading, data] of Object.entries(value.data)) {
				const nchart_heading = value.nchart_option;
				formattedSpecSheet.table_data[
					nchart_heading.replace(/^"(.+(?="$))"$/, '$1')
				] = value.desc;
				switch (heading) {
					case 'Description':
						if ('Controls' === nchart_heading) {
							formattedSpecSheet.controls['Desc'] = value.full_desc;
						}
						if (nchart_heading === 'Mounting Options') {
							formattedSpecSheet.mechanical_information['Mounting'] = data;
						}
						break;
					case 'Mounting options':
						formattedSpecSheet.mechanical_information['Mounting'] = data;
						break;
					case 'Finish':
						formattedSpecSheet.construction['Finish'] = data;
						break;
					case 'Fusing':
						formattedSpecSheet.electrical_specifications['Fusing'] = data;
						break;
					case 'Harmonics':
						formattedSpecSheet.electrical_specifications['Harmonics'] = data;
						break;
					case 'Housing':
						formattedSpecSheet.construction['Housing'] = data;
						break;
					case 'Operating Temperature':
						formattedSpecSheet.electrical_specifications[
							'Operating temperature'
						] = data;
						break;
					case 'Power Factor':
						formattedSpecSheet.electrical_specifications['Power factor'] = data;
						break;
					case 'THD':
						formattedSpecSheet.electrical_specifications['THD'] = data;
						break;
					case 'Transient protection':
						formattedSpecSheet.electrical_specifications[
							'Transient protection'
						] = data;
						break;
					case 'CCT':
						formattedSpecSheet.photometric_information['CCT'] = data;
						break;
					case 'CRI':
						formattedSpecSheet.photometric_information['CRI'] = data;
						break;
					default:
						break;
				}
			}
		}

		for (const [heading, data] of Object.entries(
			formattedSpecSheet.table_data,
		)) {
			switch (heading) {
				case 'Lens Option':
					formattedSpecSheet.construction['Lens'] = data;
					break;
				case 'Finish':
					formattedSpecSheet.construction['Color'] = data;
					break;
				case 'Hardware/Cable Options':
					if (this.productID.productSeries.includes('GRP')) {
						formattedSpecSheet.mechanical_information['Terminals'] = data;
					} else {
						formattedSpecSheet.mechanical_information['Power cord'] = data;
					}
					break;
				case 'Operating Voltage':
					formattedSpecSheet.electrical_specifications['Operating voltage'] =
						data;
					break;
			}
		}

		return formattedSpecSheet;
	};

	downloadSpecSheet = (specSheet) => {
		const pdfDocGenerator = pdfMake.createPdf(specSheet);
		pdfDocGenerator.download(`${this.productID.configName}-spec-sheet.pdf`);
	};

	getSpecSheetBrandColor = () => {
		let brandColorHex = '';

		switch (this.productID.productFamily) {
			case 'SafeSite':
				brandColorHex = '#f78f1e';
				break;
			case 'Vigilant':
				brandColorHex = '#647ca4';
				break;
			case 'DuroSite':
				brandColorHex = '#0c2340';
				break;
			case 'ProSite':
				brandColorHex = '#484950';
				break;
			case 'Reliant':
				brandColorHex = '#68Cef2';
				break;
			default:
				brandColorHex = '#0c2340';
				break;
		}
		return brandColorHex;
	};

	generateSpecSheet = () => {
		const content = this.specSheetContent();

		this.brandingColor = this.getSpecSheetBrandColor();

		const dd = {
			pageMargins: [40, 80, 40, 30],

			header: {
				table: {
					widths: ['*', '*'],
					heights: [50, 50],
					body: [
						[
							{
								svg: `<svg width="160px" height="28px" viewBox="0 0 174 42" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"> <g id="Symbols" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"> <g id="HEADER---v2" transform="translate(-112.000000, -18.000000)"> <g id="HEADER"> <g id="logo-dialight" transform="translate(112.000000, 18.000000)"> <path d="M102.545526,41.7763043 C104.290455,41.7763043 105.705,40.3660013 105.705,38.6263043 C105.705,36.8866074 104.290455,35.4763043 102.545526,35.4763043 C100.800597,35.4763043 99.3860526,36.8866074 99.3860526,38.6263043 C99.3860526,39.4617363 99.7189248,40.2629511 100.311441,40.8536907 C100.903957,41.4444303 101.707582,41.7763043 102.545526,41.7763043" id="Shape" fill="#000"/> <path d="M94.9490526,41.7763043 C96.6939818,41.7763043 98.1085263,40.3660013 98.1085263,38.6263043 C98.1085263,36.8866074 96.6939818,35.4763043 94.9490526,35.4763043 C93.2041235,35.4763043 91.7895789,36.8866074 91.7895789,38.6263043 C91.7895789,39.4617363 92.1224511,40.2629511 92.7149674,40.8536907 C93.3074836,41.4444303 94.1111081,41.7763043 94.9490526,41.7763043" id="Shape" fill="#000"/> <path d="M87.3525789,41.7763043 C89.0975081,41.7763043 90.5120526,40.3660013 90.5120526,38.6263043 C90.5120526,36.8866074 89.0975081,35.4763043 87.3525789,35.4763043 C85.6076498,35.4763043 84.1931053,36.8866074 84.1931053,38.6263043 C84.1931053,39.4617363 84.5259774,40.2629511 85.1184937,40.8536907 C85.7110099,41.4444303 86.5146344,41.7763043 87.3525789,41.7763043" id="Shape" fill="#000"/> <path d="M79.7606842,41.7763043 C81.5056133,41.7763043 82.9201579,40.3660013 82.9201579,38.6263043 C82.9201579,36.8866074 81.5056133,35.4763043 79.7606842,35.4763043 C78.0157551,35.4763043 76.6012105,36.8866074 76.6012105,38.6263043 C76.6012105,39.4617363 76.9340827,40.2629511 77.5265989,40.8536907 C78.1191152,41.4444303 78.9227397,41.7763043 79.7606842,41.7763043" id="Shape" fill="#000"/> <path d="M72.2649474,41.7763043 C74.0098765,41.7763043 75.4244211,40.3660013 75.4244211,38.6263043 C75.4244211,36.8866074 74.0098765,35.4763043 72.2649474,35.4763043 C70.5200182,35.4763043 69.1054737,36.8866074 69.1054737,38.6263043 C69.1054737,39.4617363 69.4383458,40.2629511 70.0308621,40.8536907 C70.6233784,41.4444303 71.4270028,41.7763043 72.2649474,41.7763043" id="Shape" fill="#000"/> <path d="M64.5631579,41.7763043 C66.308087,41.7763043 67.7226316,40.3660013 67.7226316,38.6263043 C67.7226316,36.8866074 66.308087,35.4763043 64.5631579,35.4763043 C62.8182288,35.4763043 61.4036842,36.8866074 61.4036842,38.6263043 C61.4036842,39.4617363 61.7365564,40.2629511 62.3290726,40.8536907 C62.9215889,41.4444303 63.7252134,41.7763043 64.5631579,41.7763043" id="Shape" fill="#000"/> <path d="M56.9758421,41.7763043 C58.7207712,41.7763043 60.1353158,40.3660013 60.1353158,38.6263043 C60.1353158,36.8866074 58.7207712,35.4763043 56.9758421,35.4763043 C55.230913,35.4763043 53.8163684,36.8866074 53.8163684,38.6263043 C53.8163684,39.4617363 54.1492406,40.2629511 54.7417568,40.8536907 C55.3342731,41.4444303 56.1378976,41.7763043 56.9758421,41.7763043" id="Shape" fill="#000"/> <path d="M49.3793684,41.7763043 C51.1242976,41.7763043 52.5388421,40.3660013 52.5388421,38.6263043 C52.5388421,36.8866074 51.1242976,35.4763043 49.3793684,35.4763043 C47.6344393,35.4763043 46.2198947,36.8866074 46.2198947,38.6263043 C46.2198947,39.4617363 46.5527669,40.2629511 47.1452832,40.8536907 C47.7377994,41.4444303 48.5414239,41.7763043 49.3793684,41.7763043" id="Shape" fill="#000"/> <path d="M41.7828947,41.7763043 C43.5278239,41.7763043 44.9423684,40.3660013 44.9423684,38.6263043 C44.9423684,36.8866074 43.5278239,35.4763043 41.7828947,35.4763043 C40.0379656,35.4763043 38.6234211,36.8866074 38.6234211,38.6263043 C38.6234211,39.4617363 38.9562932,40.2629511 39.5488095,40.8536907 C40.1413257,41.4444303 40.9449502,41.7763043 41.7828947,41.7763043" id="Shape" fill="#000"/> <path d="M33.7788947,41.7763043 C35.5238239,41.7763043 36.9383684,40.3660013 36.9383684,38.6263043 C36.9383684,36.8866074 35.5238239,35.4763043 33.7788947,35.4763043 C32.0339656,35.4763043 30.6194211,36.8866074 30.6194211,38.6263043 C30.6194211,39.4617363 30.9522932,40.2629511 31.5448095,40.8536907 C32.1373257,41.4444303 32.9409502,41.7763043 33.7788947,41.7763043" id="Shape" fill="#000"/> <path d="M26.1824211,41.7763043 C27.9273502,41.7763043 29.3418947,40.3660013 29.3418947,38.6263043 C29.3418947,36.8866074 27.9273502,35.4763043 26.1824211,35.4763043 C24.4374919,35.4763043 23.0229474,36.8866074 23.0229474,38.6263043 C23.0229474,39.4617363 23.3558195,40.2629511 23.9483358,40.8536907 C24.540852,41.4444303 25.3444765,41.7763043 26.1824211,41.7763043" id="Shape" fill="#000"/> <path d="M18.5859474,41.7763043 C20.3308765,41.7763043 21.7454211,40.3660013 21.7454211,38.6263043 C21.7454211,36.8866074 20.3308765,35.4763043 18.5859474,35.4763043 C16.8410182,35.4763043 15.4264737,36.8866074 15.4264737,38.6263043 C15.4264737,39.4617363 15.7593458,40.2629511 16.3518621,40.8536907 C16.9443784,41.4444303 17.7480028,41.7763043 18.5859474,41.7763043" id="Shape" fill="#000"/> <path d="M10.7742632,41.7763043 C12.5191923,41.7763043 13.9337368,40.3660013 13.9337368,38.6263043 C13.9337368,36.8866074 12.5191923,35.4763043 10.7742632,35.4763043 C9.02933402,35.4763043 7.61478947,36.8866074 7.61478947,38.6263043 C7.61478947,39.4617363 7.94766164,40.2629511 8.54017789,40.8536907 C9.13269414,41.4444303 9.93631864,41.7763043 10.7742632,41.7763043" id="Shape" fill="#000"/> <path d="M3.15947368,41.7763043 C4.90440282,41.7763043 6.31894737,40.3660013 6.31894737,38.6263043 C6.31894737,36.8866074 4.90440282,35.4763043 3.15947368,35.4763043 C1.41454455,35.4763043 2.13692188e-16,36.8866074 0,38.6263043 C-1.59110709e-15,39.4617363 0.332872165,40.2629511 0.925388417,40.8536907 C1.51790467,41.4444303 2.32152916,41.7763043 3.15947368,41.7763043" id="Shape" fill="#000"/> <path d="M24.7812632,0.456521739 L34.0902632,0.456521739 C36.2673887,0.41092269 38.4411033,0.650358017 40.5557368,1.16869565 C42.2349432,1.60977097 43.8067787,2.386343 45.1758947,3.45130435 C47.0272376,4.89345016 48.5204343,6.74146176 49.5396316,8.85195652 C50.5768386,10.9857103 51.1079481,13.3287057 51.0918947,15.6997826 C51.1250987,18.2629011 50.5392991,20.7963639 49.3839474,23.0863043 C48.2670068,25.2812389 46.6016933,27.1522435 44.5485789,28.518913 C43.1822447,29.4238369 41.6684862,30.0849893 40.0749474,30.4728261 C38.4692632,30.8532609 35.9951053,31.0434783 32.6524737,31.0434783 L24.7812632,31.0434783 L24.7812632,0.456521739 L24.7812632,0.456521739 Z M27.9315789,3.37826087 L27.9315789,28.1765217 L34.6305789,28.1765217 C38.7974211,28.1765217 42.0026842,27.083913 44.2463684,24.8986957 C46.4900526,22.7134783 47.6149474,19.6167391 47.6210526,15.6084783 C47.6451106,13.6934777 47.2562871,11.7956681 46.4808947,10.0434783 C45.7421927,8.38081561 44.6167429,6.91757955 43.1977895,5.775 C42.1479852,4.91493139 40.9300984,4.28216104 39.6216316,3.91695652 C37.7402144,3.49300299 35.8119364,3.31195387 33.8842105,3.37826087 L27.9315789,3.37826087 Z" id="Shape" fill="#000"/> <path d="M58.2213158,0.456521739 L58.2213158,5.22717391 L55.0526842,5.22717391 L55.0526842,0.456521739 L58.2213158,0.456521739 Z M55.0526842,31.0434783 L55.0526842,7.93891304 L58.2213158,7.93891304 L58.2213158,31.0434783 L55.0526842,31.0434783 Z" id="Shape" fill="#000"/> <path d="M84.2526316,27.3913043 C83.2091191,28.7647692 81.8252361,29.8440134 80.2368947,30.5230435 C78.5214782,31.2863387 76.6618202,31.6739679 74.7833684,31.6597826 C71.1537895,31.6597826 68.1332105,30.5002174 65.7216316,28.181087 C63.3481492,25.9598743 62.0316976,22.8387362 62.0996842,19.593913 C62.0278954,16.3073457 63.3753488,13.1483299 65.7994737,10.92 C68.266,8.59478261 71.3506842,7.43217391 75.0535263,7.43217391 C76.8574971,7.41159331 78.6445474,7.78070253 80.2918421,8.51413043 C81.851266,9.21165023 83.2123344,10.2846899 84.2526316,11.6367391 L84.2526316,7.93891304 L87.3571579,7.93891304 L87.3571579,31.0434783 L84.2526316,31.0434783 L84.2526316,27.3913043 L84.2526316,27.3913043 Z M84.2526316,19.3428261 C84.2586859,16.905073 83.238776,14.5765819 81.4411579,12.9241304 C79.745405,11.1726253 77.4082116,10.1838621 74.9665263,10.185 C72.4260138,10.1380203 69.9764435,11.1274431 68.1851053,12.9241304 C66.3388629,14.6993886 65.323981,17.1652283 65.3873684,19.7217391 C65.3421199,22.1931088 66.356565,24.5663361 68.1759474,26.2454348 C69.9629211,27.9882997 72.3754852,28.9451706 74.8749474,28.9023913 C77.3621603,28.9148614 79.7431413,27.8982091 81.4503158,26.0947826 C83.2733958,24.3261255 84.2867458,21.887841 84.2526316,19.3519565 L84.2526316,19.3428261 Z" id="Shape" fill="#000"/> <rect id="Rectangle-path" fill="#000" x="92.3985789" y="0.456521739" width="3.16863158" height="30.6006522"/> <path d="M103.910053,0.456521739 L103.910053,5.22717391 L100.736842,5.22717391 L100.736842,0.456521739 L103.910053,0.456521739 Z M100.736842,31.0434783 L100.736842,7.93891304 L103.905474,7.93891304 L103.905474,31.0434783 L100.736842,31.0434783 Z" id="Shape" fill="#000"/> <path d="M113.264842,32.9471739 C113.72858,34.2724905 114.654965,35.3873012 115.874842,36.0880435 C117.270194,36.8709049 118.853402,37.2592064 120.453789,37.211087 C123.271368,37.211087 125.531842,36.336087 127.235211,34.586087 C128.938579,32.836087 129.788737,30.4956522 129.785684,27.5647826 L129.785684,26.9576087 C128.692035,28.3950865 127.294447,29.5749541 125.692105,30.4134783 C124.129914,31.1953809 122.403179,31.5944693 120.655263,31.5776087 C116.937158,31.5776087 113.849421,30.4180435 111.392053,28.098913 C108.971513,25.8691666 107.628906,22.7097816 107.706,19.425 C107.643776,16.1769049 108.976756,13.0571178 111.369158,10.8515217 C113.789493,8.54153342 117.035759,7.2922881 120.385105,7.38195652 C122.242703,7.33712739 124.087877,7.69539787 125.792842,8.43195652 C127.383018,9.1861805 128.768115,10.3103506 129.831474,11.7097826 L129.831474,7.93891304 L132.936,7.93891304 L132.936,27.2315217 C132.936,31.2671739 131.852316,34.4202174 129.684947,36.6906522 C127.517579,38.961087 124.530579,40.0963043 120.723947,40.0963043 C118.179373,40.1664554 115.670134,39.4902303 113.507526,38.1515217 C111.591579,36.9785954 110.200097,35.1174101 109.62,32.9517391 L113.306053,32.9517391 L113.264842,32.9471739 Z M111.062368,19.3108696 C110.995674,21.8553883 111.990503,24.3135139 113.809737,26.0993478 C115.540382,27.8505923 117.903219,28.8366283 120.369079,28.8366283 C122.834939,28.8366283 125.197776,27.8505923 126.928421,26.0993478 C128.740037,24.4350254 129.76408,22.0864566 129.749053,19.6304348 C129.796692,17.1014714 128.803927,14.6632917 127.001684,12.8830435 C125.3136,11.08664 122.950251,10.0739767 120.481263,10.0891304 C117.989512,10.0533623 115.589409,11.0250171 113.828053,12.7826087 C112.022711,14.4715682 111.018059,16.8430014 111.062368,19.3108696 L111.062368,19.3108696 Z" id="Shape" fill="#000"/> <path d="M137.775947,0.456521739 L140.903368,0.456521739 L140.903368,10.6506522 C141.800772,9.6129515 142.930024,8.79982636 144.200211,8.27673913 C145.567515,7.72815915 147.030712,7.45657219 148.504421,7.47782609 C151.981368,7.47782609 154.515053,8.40608696 156.105474,10.2626087 C157.695895,12.1191304 158.491105,15.1352174 158.491105,19.3108696 L158.491105,31.0434783 L155.363684,31.0434783 L155.363684,19.2652174 C155.363684,15.9173913 154.823368,13.5876087 153.742737,12.2758696 C152.662105,10.9641304 150.856474,10.3097826 148.325842,10.3128261 C147.016168,10.2839903 145.717857,10.5606956 144.534474,11.1208696 C143.468737,11.6354613 142.571546,12.4413033 141.947368,13.4445652 C141.54546,14.1073787 141.267735,14.8375061 141.127737,15.5993478 C140.93131,16.9949002 140.851667,18.4043255 140.889632,19.8130435 L140.889632,31.0434783 L137.762211,31.0434783 L137.762211,0.456521739" id="Shape" fill="#000"/> <polyline id="Shape" fill="#000" points="165.684632 31.0571739 165.684632 10.9245652 160.982053 10.9245652 160.982053 7.93891304 165.684632 7.93891304 165.684632 0.456521739 168.853263 0.456521739 168.853263 7.93891304 173.551263 7.93891304 173.551263 10.9245652 168.853263 10.9245652 168.853263 31.0571739 165.684632 31.0571739"/> </g> </g> </g> </g> </svg>`,
								margin: [20, 12, 0, 0],
								fillColor: '#d1d2d3',
							},
							{
								text: `${this.productID.productSeries} | ${this.productID.productNumber}`,
								alignment: 'right',
								margin: [0, 20, 40, 0],
								fillColor: '#d1d2d3',
								color: '#000',
							},
						],
					],
				},
				layout: {
					hLineColor: '#d1d2d3',
					vLineColor: '#d1d2d3',
				},
			},

			footer(currentPage) {
				return {
					columns: [
						{ text: currentPage.toString(), alignment: 'left' },
						{ text: 'www.dialight.com', alignment: 'right' },
					],
					margin: [40, 0],
				};
			},

			content: [this.parseSpecSheet(content)],

			defaultStyle: {
				font: 'helveticaNeue',
			},

			styles: {
				header: {
					color: `${this.brandingColor}`,
					font: 'avantGarde',
				},
				detailHeader: {
					color: '#000',
					margin: [0, 15, 0, 5],
					font: 'avantGarde',
					fontSize: 10,
				},
				specTitle: {
					color: `${this.brandingColor}`,
					fontSize: 20,
					font: 'Roboto',
				},
				subSpecTitle: {
					color: '#8b978a',
					bold: true,
					fontSize: 14,
				},
				infoTable: {
					margin: [0, 0, 0, 15],
				},
				splitList: {
					margin: [0, 15, 0, 0],
				},
				splitListItem: {
					fontSize: 11,
					margin: [0, 7, 10, 0],
				},
				pageFootnote: {
					color: '#8b978a',
					fontSize: 5.5,
				},
				contentFootnote: {
					color: '#8b978a',
					fontSize: 9,
				},
				imageDesc: {
					fontSize: 7,
					color: '#8b978a',
				},
				tableContent: {
					fontSize: 8,
					color: '#08223f',
					alignment: 'center',
					margin: [0, 2, 0, 2],
				},
				columnHeader: {
					color: '#000',
					bold: true,
					fontSize: 8,
				},
				columnContent: {
					color: '#000',
					fontSize: 8,
				},
				disclaimer: {
					fontSize: 8,
					italics: true,
					color: '#8b978a',
					margin: [10, 5, 0, 10],
				},
			},

			images: this.selectedImages.images,
		};

		this.downloadSpecSheet(dd);
	};
}
